/**
 * @author Angel Labrada
 * @since v0.0.1
 * @date 22/11/21
 */
import React, {memo, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import {Button, Tooltip} from 'antd';
import {CaretRightOutlined} from '@ant-design/icons';
import styles from './index.less';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';
import {CopyOutlined, DeleteOutlined} from '@ant-design/icons';
import {useCrudSections} from '@/modules/templates/hooks/useSections';
import SectionTitleEdit from '@/modules/templates/components/TemplateSectionsDrawer/components/Section';
import {useAuditCalc} from '@/modules/auditorAudits/hooks/useAuditCalc';
import {useRunAuditOnlineStatus} from '@/contexts/RunAuditOnlineStatusProvider';

const SectionTitle = ({section, runAudit, activeKey, isCover}) => {
  const {t} = useTranslation('templates');
  const { sectionScore: sectionScoreCtx } = useAuditCalc();
  const {duplicate, isDuplicating, remove, isRemoving} = useCrudSections();
  const { online } = useRunAuditOnlineStatus();

  const onDuplicate = useCallback(async () => {
    await duplicate({sectionId: section?._id, isNewDuplicate: true});
  }, [duplicate, section?._id]);

  const onRemoveDuplicatedSection = useCallback(async () => {
    await remove(section?._id);
  }, [remove, section?._id]);

  const sectionTitle = useMemo(() => {
    if (section?.title === 'Introduction') {
      return t(section?.title);
    }
    return section?.title;
  }, [section, t]);

  const sectionScore = useMemo(() => {
    if (sectionScoreCtx?.id && sectionScoreCtx?.id === section?._id) {
      return {
        finalScore: sectionScoreCtx?.finalScore ? sectionScoreCtx?.finalScore : section?.finalScore,
        totalScore: sectionScoreCtx?.totalScore ? sectionScoreCtx?.totalScore : section?.totalScore,
      };
    }
    return {
      finalScore: section?.finalScore,
      totalScore: section?.totalScore,
    };
  }, [section?._id, section?.finalScore, section?.totalScore, sectionScoreCtx?.finalScore, sectionScoreCtx?.id, sectionScoreCtx?.totalScore]);

  return (
    <div className={runAudit ? isCover ? styles.sectionHeader : styles.sectionPanelHeader : styles.sectionHeader}>
      <div className="flex items-center justify-start" onClick={(e) => e.preventDefault()}>
        {runAudit && <>{!activeKey?.length ? <CaretRightOutlined /> : <CaretRightOutlined rotate={90} />}</>}
        {runAudit && section?.title !== 'Introduction' && <div className={classNames(styles.title, 'ml-3')}><SectionTitleEdit  section={section} /></div>}
        {runAudit && section?.title === 'Introduction' && <div className={classNames(styles.title, runAudit ? 'ml-3' : '')}>{sectionTitle}</div>}
      </div>
      {runAudit && !isCover && (
        <div
          className="header-score flex items-center gap-3"
        >
          <div
            className="font-bold header-score"
            style={{fontSize: '1.2rem'}}
          >
            {`${sectionScore?.finalScore || 0} / ${sectionScore?.totalScore || 0} (${Number(sectionScore?.finalScore * 100 / sectionScore?.totalScore || 0).toFixed(1)}%)`}
          </div>

          {section?.duplicable && <div>
            <Tooltip title={t('duplicable')}>
              <Button
                type="dashed"
                disabled={isDuplicating || !online}
                loading={isDuplicating}
                onClick={onDuplicate}
                className={classNames(styles.duplicableButton)}
                style={{background: 'transparent', color: '#fff'}}
                icon={<CopyOutlined/>}
              />
            </Tooltip>
          </div>}
          {section?.isDuplicated && <div>
            <Tooltip title={t('removeDuplicated')}>
              <Button
                type="dashed"
                disabled={isRemoving || !online}
                loading={isRemoving}
                onClick={onRemoveDuplicatedSection}
                className={classNames(styles.removeDuplicatedButton)}
                style={{background: 'transparent', color: '#fff'}}
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </div>}
        </div>
      )}
    </div>
  );
};

SectionTitle.propTypes = {
  section: PropTypes.object,
  runAudit: PropTypes.bool,
  isCover: PropTypes.bool,
  activeKey: PropTypes.array,
};

export default memo(SectionTitle);
